import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, where } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { Alert, Button, Card, Form, Modal, Spinner } from "react-bootstrap";

import { db, storage } from "../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../shared/auth/AuthProvider";
import {
  AFFILIATE_APPLICATION_STATUSES,
  AFFILIATE_COLLECTIONS,
} from "../../../shared/utils/affiliateSchema";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_QR_SIZE = 5 * 1024 * 1024;

function getFileExt(fileName = "") {
  return String(fileName).split(".").pop()?.toLowerCase() || "jpg";
}

export default function AffiliateApplyPage() {
  const navigate = useNavigate();
  const { user, userDoc } = useAuth();
  const uid = user?.uid || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [application, setApplication] = useState(null);
  const [affiliate, setAffiliate] = useState(null);
  const [qrFile, setQrFile] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [form, setForm] = useState({
    fullName: "",
    phoneNumber: "",
    contactEmail: "",
    address: "",
    message: "",
    bankName: "",
    bankAccountName: "",
    bankAccountNumber: "",
  });

  useEffect(() => {
    if (!uid) return;

    const loadApplication = async () => {
      try {
        setLoading(true);
        const affiliateSnap = await getDoc(doc(db, AFFILIATE_COLLECTIONS.affiliates, uid));
        const affiliateData = affiliateSnap.exists()
          ? { id: affiliateSnap.id, ...affiliateSnap.data() }
          : null;

        setAffiliate(affiliateData);

        if (affiliateData?.status === "approved") {
          navigate("/account/affiliate/profile", { replace: true });
          return;
        }

        const q = query(
          collection(db, AFFILIATE_COLLECTIONS.applications),
          where("userId", "==", uid)
        );
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const latest = rows.sort((a, b) => {
          const ta = a?.createdAt?.toMillis?.() || 0;
          const tb = b?.createdAt?.toMillis?.() || 0;
          return tb - ta;
        })[0] || null;

        setApplication(latest);
        setForm((prev) => ({
          ...prev,
          fullName:
            latest?.profile?.fullName ||
            userDoc?.enrollProfile?.fullName ||
            userDoc?.displayName ||
            user?.displayName ||
            prev.fullName,
          phoneNumber:
            latest?.profile?.phoneNumber ||
            userDoc?.enrollProfile?.phoneNumber ||
            userDoc?.phoneNumber ||
            prev.phoneNumber,
          contactEmail: latest?.contact?.email || user?.email || userDoc?.email || prev.contactEmail,
          address:
            latest?.profile?.address ||
            userDoc?.enrollProfile?.shippingAddress ||
            userDoc?.shippingAddress ||
            prev.address,
          message: latest?.message || prev.message,
          bankName: latest?.bankAccount?.bankName || prev.bankName,
          bankAccountName: latest?.bankAccount?.accountName || prev.bankAccountName,
          bankAccountNumber: latest?.bankAccount?.accountNumber || prev.bankAccountNumber,
        }));
      } catch (e) {
        console.error(e);
        setError(e?.message || "โหลดคำขอ Affiliate ไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [uid, user, userDoc, navigate]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onPickQr = (e) => {
    const file = e.target.files?.[0] || null;
    setError("");
    setQrFile(null);

    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      e.target.value = "";
      setError("กรุณาอัปโหลด QR เป็นไฟล์ JPG, PNG หรือ WEBP เท่านั้น");
      return;
    }
    if (file.size > MAX_QR_SIZE) {
      e.target.value = "";
      setError("ไฟล์ QR มีขนาดใหญ่เกินไป กรุณาใช้ไฟล์ไม่เกิน 5MB");
      return;
    }

    setQrFile(file);
  };

  const uploadQrIfNeeded = async () => {
    if (!qrFile || !uid) return { imageUrl: "", storagePath: "" };
    const ext = getFileExt(qrFile.name);
    const storagePath = `affiliate-qr/${uid}/application_${Date.now()}.${ext}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, qrFile, { contentType: qrFile.type || "image/jpeg" });
    const imageUrl = await getDownloadURL(storageRef);
    return { imageUrl, storagePath };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!uid) return;

    setError("");
    setSuccess("");

    if (!form.fullName.trim() || !form.phoneNumber.trim() || !form.contactEmail.trim()) {
      setError("กรุณากรอกชื่อ เบอร์โทรศัพท์ และอีเมลติดต่อ");
      return;
    }
    if (!form.bankName.trim() || !form.bankAccountName.trim() || !form.bankAccountNumber.trim()) {
      setError("กรุณากรอกข้อมูลบัญชีธนาคารให้ครบถ้วน");
      return;
    }
    if (application?.status === AFFILIATE_APPLICATION_STATUSES.pending) {
      setError("คุณมีคำขอที่รอตรวจสอบอยู่แล้ว");
      return;
    }

    try {
      setSaving(true);
      const qrCode = await uploadQrIfNeeded();

      await addDoc(collection(db, AFFILIATE_COLLECTIONS.applications), {
        userId: uid,
        status: AFFILIATE_APPLICATION_STATUSES.pending,
        profile: {
          fullName: form.fullName.trim(),
          phoneNumber: form.phoneNumber.trim(),
          address: form.address.trim(),
        },
        contact: {
          email: form.contactEmail.trim(),
        },
        bankAccount: {
          bankName: form.bankName.trim(),
          accountName: form.bankAccountName.trim(),
          accountNumber: form.bankAccountNumber.trim(),
        },
        qrCode: {
          imageUrl: qrCode.imageUrl,
          storagePath: qrCode.storagePath,
          updatedAt: qrCode.imageUrl ? new Date() : null,
        },
        message: form.message.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSuccess("ส่งคำขอ Affiliate เรียบร้อยแล้ว");
      setQrFile(null);
      setApplication({ status: AFFILIATE_APPLICATION_STATUSES.pending });
      setShowSuccessModal(true);
    } catch (e2) {
      console.error(e2);
      setError(e2?.message || "ส่งคำขอไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-4 text-center text-muted">
        <Spinner animation="border" size="sm" /> กำลังโหลดข้อมูล...
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-1">สมัคร Affiliate</h4>
      <div className="text-muted small mb-3">ส่งข้อมูลเพื่อให้ทีมงานตรวจสอบและอนุมัติ</div>

      {application?.status ? (
        <Alert variant={application.status === "rejected" ? "danger" : "info"}>
          สถานะคำขอล่าสุด: <strong>{application.status}</strong>
        </Alert>
      ) : null}
      {affiliate?.status && affiliate.status !== "approved" ? (
        <Alert variant={affiliate.status === "suspended" ? "warning" : "secondary"}>
          สถานะ Affiliate ปัจจุบัน: <strong>{affiliate.status}</strong>
        </Alert>
      ) : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Card className="border">
        <Card.Body>
          <Form onSubmit={onSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อ-นามสกุล *</Form.Label>
              <Form.Control value={form.fullName} onChange={(e) => setField("fullName", e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>เบอร์โทรศัพท์ *</Form.Label>
              <Form.Control value={form.phoneNumber} onChange={(e) => setField("phoneNumber", e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>อีเมลติดต่อ *</Form.Label>
              <Form.Control value={form.contactEmail} onChange={(e) => setField("contactEmail", e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>ที่อยู่</Form.Label>
              <Form.Control as="textarea" rows={3} value={form.address} onChange={(e) => setField("address", e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>เหตุผลในการสมัคร (ถ้ามี)</Form.Label>
              <Form.Control as="textarea" rows={3} value={form.message} onChange={(e) => setField("message", e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>ธนาคาร *</Form.Label>
              <Form.Control value={form.bankName} onChange={(e) => setField("bankName", e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>ชื่อบัญชี *</Form.Label>
              <Form.Control value={form.bankAccountName} onChange={(e) => setField("bankAccountName", e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>เลขบัญชี *</Form.Label>
              <Form.Control value={form.bankAccountNumber} onChange={(e) => setField("bankAccountNumber", e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>QR Code สำหรับรับเงิน</Form.Label>
              <Form.Control type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickQr} />
            </Form.Group>

            <Button type="submit" disabled={saving || application?.status === AFFILIATE_APPLICATION_STATUSES.pending}>
              {saving ? "กำลังส่ง..." : "ส่งคำขอ"}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Modal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          navigate("/account/affiliate/profile");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>ส่งคำขอสำเร็จ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ระบบได้รับคำขอสมัคร Affiliate ของคุณแล้ว กรุณารอแอดมินตรวจสอบ
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              setShowSuccessModal(false);
              navigate("/account/affiliate/profile");
            }}
          >
            ตกลง
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
